import { request } from '../api';

// 指派类型枚举
export enum AssignTypeEnum {
  USER = 'user',
  DEPARTMENT = 'department',
  ROLE = 'role'
}

// 指派状态枚举
export enum AssignStatusEnum {
  ACTIVE = 'active',
  CANCELLED = 'cancelled'
}

// 用户信息
export interface UserInfo {
  id: string;
  name: string;
}

// 场景指派信息
export interface ScenarioAssignInfo {
  id: string;
  scenario_id: string;
  scenario_name: string;
  assign_type: AssignTypeEnum;
  target_id: string;
  target_name: string;
  status: AssignStatusEnum;
  assign_description?: string;
  effective_start_time?: string;
  effective_end_time?: string;
  assigned_by: UserInfo;
  assigned_at: string;
  cancelled_by?: UserInfo;
  cancelled_at?: string;
  cancel_reason?: string;
}

// 场景指派列表响应
export interface ScenarioAssignListResponse {
  list?: ScenarioAssignInfo[];
  assigns?: ScenarioAssignInfo[];
  total: number;
  current: number;
  pageSize: number;
}

// 获取目标的指派列表
export async function getTargetAssigns(
  assignType: AssignTypeEnum,
  targetId: string,
  params?: {
    status?: AssignStatusEnum;
    page?: number;
    pageSize?: number;
  }
) {
  return request<ScenarioAssignListResponse>(
    `/scenarios-assign/target/${assignType}/${targetId}`,
    {
      method: 'GET',
      params
    }
  );
}

// 获取场景指派详情
export async function getAssignDetail(assignId: string) {
  return request<ScenarioAssignInfo>(
    `/scenarios-assign/${assignId}`,
    {
      method: 'GET'
    }
  );
}

// 获取当前用户的指派列表
export async function getMyAssigns(
  params?: {
    status?: AssignStatusEnum;
    page?: number;
    pageSize?: number;
  }
) {
  return request<ScenarioAssignListResponse>(
    `/scenarios-assign/my-assigns`,
    {
      method: 'GET',
      params
    }
  );
}

// AI角色数据结构
export interface RoleData {
  name: string;
  description?: string;
  avatar?: string;
  communication_style?: string;
  background?: string;
  personality_traits?: Record<string, number>;
  personality_type?: string;
  voice_id?: string;
  tags?: string[];
  source_type?: string;
  knowledge_level?: string[];
  taboos_objections?: string[];
}

// AI角色信息
export interface AIRoleInScenario {
  scenario_role_id: string;
  template_id?: string;
  template_version?: string;
  template_name?: string;
  customization_level: string;
  is_custom_role: boolean;
  role_data: RoleData;
  customized_fields: any[];
  created_at?: string;
  updated_at?: string;
}

// 对话环节响应
export interface ConversationStepResponse {
  id: string;
  step_name: string;
  student_objective: string;
  ai_task: string;
  max_rounds: number;
  sort_order: number;
}



// 完成规则枚举
export enum CompletionRuleEnum {
  ALL_STEPS = 'all_steps',
  TIME_BASED = 'time_based',
  SCORE_BASED = 'score_based'
}

// 场景状态枚举
export enum ScenarioStatusEnum {
  DRAFT = 0,
  PUBLISHED = 1,
  ARCHIVED = 2
}

// 场景详情信息
export interface ScenarioDetail {
  id: string;
  name: string;
  description: string;
  scenario_cover: string;
  tags: string[];
  key_points: string;
  scene_lang?: string; // 场景语言
  status: ScenarioStatusEnum;
  version: string;
  version_description: string;
  current_step: number;
  
  // 步骤2：AI角色设定
  ai_roles: AIRoleInScenario[];
  ai_roles_overall_description?: string;
  
  // 步骤3：流程设置
  opening_dialogue?: string;
  closing_dialogue?: string;
  conversation_knowledge_base_ids?: string[];
  
  // 步骤4：评分标准
  scoring_criteria_config?: Record<string, any>;
  scoring_knowledge_base_ids?: string[];
  
  // 陪练设置
  practice_time_start?: string;
  practice_time_end?: string;
  min_practice_duration?: number;
  max_practice_duration?: number;
  max_practice_attempts?: number;
  completion_rule?: CompletionRuleEnum;
  
  // 用户相关信息
  user_practice_count?: number; // 用户练习次数
  passing_score?: number; // 成绩要求

  created_by: UserInfo;
  created_at: string;
  updated_at: string;
}

// 获取场景详情（学员端）
export async function getScenarioDetail(scenarioId: string) {
  return request<ScenarioDetail>(
    `/scenarios/${scenarioId}/student-detail`,
    {
      method: 'GET'
    }
  );
}

// 检查指派权限
export async function checkAssignPermission(
  scenarioId: string,
  assignType: AssignTypeEnum,
  targetId: string
) {
  return request<{
    scenario_id: string;
    assign_type: AssignTypeEnum;
    target_id: string;
    has_permission: boolean;
    checked_at: string;
  }>(
    `/scenarios-assign/check-permission/${scenarioId}/${assignType}/${targetId}`,
    {
      method: 'GET'
    }
  );
}